import { Fragment, ReactElement } from 'react';

import {
  FormField,
  InputTypes,
  IntegrationThemes,
  ValidationStatuses,
} from '@cashier/cashier-model';
import { useUIConfiguration } from '@cashier/cashier-shared';
import { cn, InputGenerator, Select } from '@cashier/cashier-ui';

import {
  useCashierConfiguration,
  useFieldToucher,
  useFormPayloadItem,
} from '../../../hooks';
import { DynamicDropdown } from '../../dynamicDropdown';

import styles from './styles.module.scss';

interface FieldsGeneratorProps {
  field: FormField;
  version: 'v2' | 'v3';
}

enum SpecificComponentTypes {
  DynamicSelect = 'dynamic_select',
  Select = 'select',
  Input = 'input',
}

const COMPONENT_TYPE_MAP: { [key: string]: SpecificComponentTypes } = {
  [InputTypes.DynamicSelect]: SpecificComponentTypes.DynamicSelect,
  [InputTypes.Select]: SpecificComponentTypes.Select,
  [InputTypes.Number]: SpecificComponentTypes.Input,
};

export const FieldWrapper = ({
  field,
  version,
}: FieldsGeneratorProps): ReactElement => {
  const { label, name, type, value } = field;

  const { touch } = useFieldToucher();
  const { direction } = useCashierConfiguration();

  const { options } = field.params || {};

  const { validationStatus, isDisabled } = useFormPayloadItem(name);
  const labelPosition = useUIConfiguration('input', 'label');

  return (
    <Fragment key={`${label}_${name}`}>
      {COMPONENT_TYPE_MAP[field.type] ===
        SpecificComponentTypes.DynamicSelect && (
        <div className={styles.row}>
          <div className={cn([styles.col])}>
            <DynamicDropdown
              direction={direction}
              theme={IntegrationThemes.Platform}
              version={version}
              field={field}
              onChange={(value) =>
                touch({ ...field, value }, { interactionCompleted: true })
              }
              isDisabled={isDisabled}
            />
          </div>
        </div>
      )}
      {COMPONENT_TYPE_MAP[field.type] === SpecificComponentTypes.Select && (
        <div className={styles.row}>
          <div className={cn([styles.col])}>
            <Select
              direction={direction}
              theme={IntegrationThemes.Platform}
              version={version}
              label={label}
              options={options || []}
              name={name}
              defaultValue={value as string}
              isError={validationStatus === ValidationStatuses.FAILED}
              isDisabled={isDisabled}
              onChange={(value) =>
                touch({ ...field, value }, { interactionCompleted: true })
              }
            />
          </div>
        </div>
      )}
      {COMPONENT_TYPE_MAP[field.type] === SpecificComponentTypes.Input && (
        <div className={styles.row}>
          <InputGenerator
            labelPosition={labelPosition}
            direction={direction}
            validationStatus={validationStatus}
            field={field}
            onChange={(value: string, isFocused: boolean) =>
              touch({ ...field, value }, { interactionCompleted: !isFocused })
            }
            isDisabled={isDisabled}
          />
        </div>
      )}
      {!COMPONENT_TYPE_MAP[field.type] && (
        <div
          className={cn({
            [styles.row]: true,
            [styles.hidden]: type === InputTypes.Hidden,
          })}
        >
          <div className={cn([styles.col, styles.full])}>
            <InputGenerator
              direction={direction}
              labelPosition={labelPosition}
              field={field}
              validationStatus={validationStatus}
              isDisabled={isDisabled}
              onChange={(value: string, isFocused: boolean) =>
                touch({ ...field, value }, { interactionCompleted: !isFocused })
              }
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};
